import React, { useState, useEffect, useRef } from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import SelectionCard from '../SelectionCard';

// === Custom Modal ===
function ConfirmationModal({ open, onConfirm, onCancel, message }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-lg border border-gray-200 text-center space-y-5">
        <div className="text-orange-500 text-3xl mb-0">⚠️</div>
        <div className="text-lg font-medium">{message}</div>
        <div className="flex space-x-4 justify-center mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium"
          >Stay on this step</button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-[#FF5841] text-white hover:bg-[#E5503A] font-medium"
          >Leave anyway</button>
        </div>
      </div>
    </div>
  );
}

function DropzoneUploadBox({ label, name, files, onFiles, canPickFiles, errorMsg }) {
  const inputRef = useRef();
  
  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    const currentFiles = Array.from(files);
    const fileMap = {};
    currentFiles.concat(newFiles).forEach(f => fileMap[f.name + f.size] = f);
    onFiles(Object.values(fileMap));
  };

  const onAreaClick = (e) => {
    if (!canPickFiles) {
      e.preventDefault();
      return;
    }
    inputRef.current && inputRef.current.click();
  };

  return (
    <div
      className={"relative border-2 border-dashed rounded-lg py-8 px-6 mb-2 bg-[#fcf7f4] text-center transition-colors hover:border-[#FF5841]"}
      onClick={onAreaClick}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={e => {
        e.preventDefault();
        if (!canPickFiles) return;
        handleFiles(e.dataTransfer.files);
      }}
      style={{ cursor: canPickFiles ? "pointer" : "not-allowed", opacity: canPickFiles ? 1 : 0.6 }}
    >
      <div className="text-6xl mb-2 select-none">📁</div>
      <div className="text-base mb-2">{label}</div>
      <input
        ref={inputRef}
        type="file"
        name={name}
        style={{ display: "none" }}
        multiple
        accept=".png,.svg,.ai,.eps,.pdf,.jpg,.jpeg"
        onChange={e => handleFiles(e.target.files)}
        disabled={!canPickFiles}
      />
      <button
        type="button"
        className="font-semibold text-[#FF5841] hover:underline block mx-auto mb-2"
        style={{ background: "none", border: "none" }}
        onClick={e => {
          e.stopPropagation();
          if (canPickFiles) inputRef.current && inputRef.current.click();
        }}
        disabled={!canPickFiles}
      >
        Choose files
      </button>
      <div className="text-xs text-gray-500 mb-2">Accepted: PNG, SVG, AI, EPS, PDF, JPG — Max 10MB each</div>
      {errorMsg && (
        <div className="text-16px font-bold text-[#ED4346] mt-1">
          {errorMsg}
        </div>
      )}
      {files.length > 0 && (
        <div className="text-left max-w-xs mx-auto mt-2">
          <div className="font-semibold mb-1">Selected files:</div>
          <ul>
            {files.map((f,i) => <li key={i}>{f.name}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Step3BrandAssets({ onLeaveStep }) {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step3 || {};
  const userEmail = formData.step1?.email || '';
  const userBusiness = formData.step1?.businessName || '';

  // Upload file state
  const [logoFiles, setLogoFiles] = useState([]);
  const [refreshLogoFiles, setRefreshLogoFiles] = useState([]);
  const [assetsFiles, setAssetsFiles] = useState([]);
  // Each group's own local status
  const [logoMsg, setLogoMsg] = useState('');
  const [refreshMsg, setRefreshMsg] = useState('');
  const [assetsMsg, setAssetsMsg] = useState('');
  const [pickError, setPickError] = useState(''); // shown if pick forbidden
  const [pendingUploads, setPendingUploads] = useState({logo: false, refresh: false, assets: false});
  // Modal state
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [attemptedLeave, setAttemptedLeave] = useState(false);

  // For upload PHP path --- NOTE: update if needed!
  const uploadUrl = "https://forms.primehost.co.za/forms/upload-files.php";

  // Upload trigger -- separate for each group
  const uploadFiles = async (files, tracker, msgSetter) => {
    if (!files.length || !userEmail) return;
    msgSetter('');
    setPendingUploads(pu => ({...pu, [tracker]: true}));
    const form = new FormData();
    files.forEach(file => form.append('files[]', file));
    form.append('email', userEmail);
    if (userBusiness) form.append('businessName', userBusiness);

    try {
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: form,
      });
      const json = await res.json();
      if (json.status === "success") {
        msgSetter("Files uploaded successfully!");
        // Empty the filed group on success
        if (tracker === "logo") setLogoFiles([]);
        if (tracker === "refresh") setRefreshLogoFiles([]);
        if (tracker === "assets") setAssetsFiles([]);
      } else {
        msgSetter(json.message || "Failed to upload. Try again.");
      }
    } catch (e) {
      msgSetter("Failed to upload. Try again.");
    }
    setPendingUploads(pu => ({...pu, [tracker]: false}));
    setTimeout(() => msgSetter(''), 4000);
  };

  // Prevent picking files if no email
  useEffect(() => {
    if (pickError) {
      const timer = setTimeout(() => setPickError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [pickError]);

  // Any pending files but not uploaded?
  const hasPendingFiles = (
    (logoFiles && logoFiles.length)
    || (refreshLogoFiles && refreshLogoFiles.length)
    || (assetsFiles && assetsFiles.length)
  );

  useEffect(() => {
    const handler = (e) => {
      if (hasPendingFiles) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPendingFiles]);

  const handleLeaveStep = (e) => {
    if (hasPendingFiles) {
      setShowLeaveModal(true);
      setAttemptedLeave(true);
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      return false;
    }
    if (typeof onLeaveStep === 'function') onLeaveStep();
    return true;
  };

  const confirmLeave = () => {
    setShowLeaveModal(false);
    setLogoFiles([]);
    setRefreshLogoFiles([]);
    setAssetsFiles([]);
    if (attemptedLeave && typeof onLeaveStep === 'function') onLeaveStep();
    setAttemptedLeave(false);
  };
  const cancelLeave = () => {
    setShowLeaveModal(false);
    setAttemptedLeave(false);
  };

  const logoOptions = [
    { id: 'Existing', title: 'Yes, I have a logo', description: 'I will upload my existing logo files' },
    { id: 'New', title: 'No, I need a logo designed', description: 'We can create a professional logo for you' },
    { id: 'Refresh', title: 'I have a logo but want to refresh it', description: 'Keep the essence but modernise the design' }
  ];

  const logoStyles = [
    { id: 'text-based', title: 'Text-based/Wordmark', description: 'Company name styled as the logo' },
    { id: 'icon-text', title: 'Icon + Text combination', description: 'Symbol paired with company name' },
    { id: 'abstract', title: 'Abstract symbol', description: 'Geometric or conceptual design' },
    { id: 'illustrative', title: 'Illustrative/Pictorial', description: 'Recognisable image or illustration' },
    { id: 'minimalist', title: 'Minimalist', description: 'Clean, simple, uncluttered design' },
    { id: 'bold-modern', title: 'Bold/Modern', description: 'Strong, contemporary appearance' },
    { id: 'vintage-classic', title: 'Vintage/Classic', description: 'Traditional, timeless feel' },
    { id: 'playful-creative', title: 'Playful/Creative', description: 'Fun, artistic, unconventional' }
  ];

  const brandAssetOptions = [
    { id: 'Guidelines', title: 'I have brand guidelines', description: 'Colours, fonts, style guide' },
    { id: 'Photos', title: 'I have professional photos/images to use', description: 'High-quality images for the website' },
    { id: 'Graphics', title: 'I have custom icons or graphics', description: 'Unique visual elements' }
  ];

  const handleInputChange = (field, value) => {
    updateFormData('step3', { [field]: value });
  };

  const handleStyleToggle = (styleId) => {
    const currentStyles = stepData.logoStyles || [];
    const updatedStyles = currentStyles.includes(styleId)
      ? currentStyles.filter(id => id !== styleId)
      : [...currentStyles, styleId];
    handleInputChange('logoStyles', updatedStyles);
  };

  const handleBrandAssetToggle = (assetId) => {
    const currentAssets = stepData.brandAssets || [];
    const updatedAssets = currentAssets.includes(assetId)
      ? currentAssets.filter(id => id !== assetId)
      : [...currentAssets, assetId];
    handleInputChange('brandAssets', updatedAssets);
  };

  // wrapped for dropzones
  const forbidPick = () => {
    setPickError("Please enter your email address in Step 1 before uploading files.");
  };

  return (
    <StepLayout
      title="Time to showcase your brand's treasures! 💎"
      stepNumber={3}
      totalSteps={7}
      nextButtonText="Shape my website"
      nextStep={4}
      prevStep={2}
      onLeaveStep={handleLeaveStep}
    >
      <ConfirmationModal
        open={showLeaveModal}
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
        message="You have selected files to upload but haven’t uploaded them yet. If you leave this page, your selection will be lost. Continue?"
      />
      <div className="p-4 bg-[#F9FAFB] border-l-4 border-[#FF5841] rounded-lg mb-8">
        <p className="text-gray-700 leading-relaxed">
          Whether you have a complete brand toolkit or you are starting fresh, this step helps us understand your current identity. If you already have logos, colours, or other brand materials, brilliant! If not, no worries at all, we create stunning brand identities too.
        </p>
      </div>
      <div className="space-y-8">
        {/* Logo Status */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Do you have a logo?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {logoOptions.map((option) => (
              <SelectionCard
                key={option.id}
                title={option.title}
                description={option.description}
                selected={stepData.logoStatus === option.id}
                onClick={() => handleInputChange('logoStatus', option.id)}
              />
            ))}
          </div>
          {pickError && (
            <div className="text-sm text-orange-600 text-center mb-3">{pickError}</div>
          )}
          {/* Conditional Content Based on Logo Status */}
          {stepData.logoStatus === 'Existing' && (
            <div className="mt-6 space-y-4">
              <DropzoneUploadBox
                label="Upload your logo files"
                name="logoUpload"
                files={logoFiles}
                onFiles={setLogoFiles}
                canPickFiles={!!userEmail}
                errorMsg={!userEmail ? "⚠️ Please fill in your email on Step 1 to upload." : ""}
              />
              {!!logoFiles.length && (
  <div className="flex flex-col items-center">
    <div className="mb-3 text-sm text-orange-600 italic text-center">
      Click the <b>Upload files</b> button before leaving this page to submit your files and avoid losing your selections.
    </div>
    <button
      type="button"
      className="bg-[#FF5841] text-white px-6 py-2 rounded-lg font-semibold mt-2 mb-0 w-auto max-w-xs"
      disabled={pendingUploads.logo}
      onClick={() => uploadFiles(logoFiles, 'logo', setLogoMsg)}
    >
      {pendingUploads.logo ? 'Uploading...' : 'Upload files'}
    </button>
  </div>
)}
              {logoMsg && (
  <div
    className={
      "text-center mt-3 " +
      (logoMsg.includes("success") ?
        "text-green-600 font-semibold" :
        "text-orange-600 font-semibold"
      )}
  >
    {logoMsg}
  </div>
)}
              <p className="text-base">
                Alternatively, email them to <a href="mailto:design@primehost.co.za" className="text-[#FF5841] hover:underline font-medium">design@primehost.co.za</a>
              </p>
            </div>
          )}

          {stepData.logoStatus === 'New' && (
            <div className="mt-6 space-y-6">
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">
                  Logo design ideas
                </label>
                <textarea
                  value={stepData.logoIdeas || ''}
                  onChange={(e) => handleInputChange('logoIdeas', e.target.value)}
                  placeholder="Describe your vision for the logo, any specific elements, symbols, or concepts you would like to include..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
                />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-4 text-17px">Logo style preferences</h4>
                <p className="text-16px text-gray-600 mb-4">Select the styles that appeal to you (multiple selections allowed)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {logoStyles.map((style) => (
                    <SelectionCard
                      key={style.id}
                      title={style.title}
                      description={style.description}
                      selected={(stepData.logoStyles || []).includes(style.id)}
                      onClick={() => handleStyleToggle(style.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {stepData.logoStatus === 'Refresh' && (
            <div className="mt-6 space-y-4">
              <DropzoneUploadBox
                label="Upload your current logo"
                name="refreshLogoUpload"
                files={refreshLogoFiles}
                onFiles={setRefreshLogoFiles}
                canPickFiles={!!userEmail}
                errorMsg={!userEmail ? "⚠️ Please fill in your email on Step 1 to upload." : ""}
              />
              {!!refreshLogoFiles.length && (
  <div className="flex flex-col items-center">
    <div className="mb-3 text-sm text-orange-600 italic text-center">
      Click the <b>Upload files</b> button before leaving this page to submit your files and avoid losing your selections.
    </div>
    <button
      type="button"
      className="bg-[#FF5841] text-white px-6 py-2 rounded-lg font-semibold mt-2 mb-0 w-auto max-w-xs"
      disabled={pendingUploads.refresh}
      onClick={() => uploadFiles(refreshLogoFiles, 'refresh', setRefreshMsg)}
    >
      {pendingUploads.refresh ? 'Uploading...' : 'Upload files'}
    </button>
  </div>
)}
              {refreshMsg && (
  <div
    className={
      "text-center mt-3 " +
      (refreshMsg.includes("success") ?
        "text-green-600 font-semibold" :
        "text-orange-600 font-semibold"
      )}
  >
    {refreshMsg}
  </div>
)}
              <p className="text-base">
                Alternatively, email them to <a href="mailto:design@primehost.co.za" className="text-[#FF5841] hover:underline font-medium">design@primehost.co.za</a>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  What would you like to change?
                </label>
                <textarea
                  value={stepData.logoChanges || ''}
                  onChange={(e) => handleInputChange('logoChanges', e.target.value)}
                  placeholder="Describe what aspects of your current logo you would like to modernise or improve..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Other Brand Assets */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Other brand assets (Optional)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {brandAssetOptions.map((asset) => (
              <SelectionCard
                key={asset.id}
                title={asset.title}
                description={asset.description}
                selected={(stepData.brandAssets || []).includes(asset.id)}
                onClick={() => handleBrandAssetToggle(asset.id)}
              />
            ))}
          </div>

          {(stepData.brandAssets && stepData.brandAssets.length > 0) && (
            <div className="mt-6 space-y-2">
              <DropzoneUploadBox
                label="Upload your brand assets, images and/or graphics"
                name="assetsUpload"
                files={assetsFiles}
                onFiles={setAssetsFiles}
                canPickFiles={!!userEmail}
                errorMsg={!userEmail ? "⚠️ Please fill in your email on Step 1 to upload." : ""}
              />
              {!!assetsFiles.length && (
  <div className="flex flex-col items-center">
    <div className="mb-3 text-sm text-orange-600 italic text-center">
      Click the <b>Upload files</b> button before leaving this page to submit your files and avoid losing your selections.
    </div>
    <button
      type="button"
      className="bg-[#FF5841] text-white px-6 py-2 rounded-lg font-semibold mt-2 mb-0 w-auto max-w-xs"
      disabled={pendingUploads.assets}
      onClick={() => uploadFiles(assetsFiles, 'assets', setAssetsMsg)}
    >
      {pendingUploads.assets ? 'Uploading...' : 'Upload files'}
    </button>
  </div>
)}
             {assetsMsg && (
  <div
    className={
      "text-center mt-3 " +
      (assetsMsg.includes("success") ?
        "text-green-600 font-semibold" :
        "text-orange-600 font-semibold"
      )}
  >
    {assetsMsg}
  </div>
)}
            </div>
          )}
        </div>
      </div>
    </StepLayout>
  );
}

export default Step3BrandAssets;